import React, { useState, useEffect } from "react";
import useSWR, { mutate } from "swr";
import { toast } from "sonner";
import { Hammer, Loader2, Pencil, Check, X } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { Skeleton, StatusPill, Empty } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function CRMConstruction() {
  const { data: projects } = useSWR("/projects", fetcher);
  const [projectId, setProjectId] = useState("");
  const subKey = projectId ? `/subprojects?project_id=${projectId}` : null;
  const { data: subprojects } = useSWR(subKey, fetcher);
  const [subId, setSubId] = useState("");
  const msKey = subId ? `/milestones?subproject_id=${subId}` : null;
  const { data: milestones } = useSWR(msKey, fetcher);

  // Which milestone is currently in edit mode + the draft value
  const [editing, setEditing] = useState(null); // milestone_id
  const [draftPct, setDraftPct] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (projects && projects.length && !projectId) {
      setProjectId(String(projects[0].project_id));
    }
  }, [projects, projectId]);

  useEffect(() => {
    if (subprojects && subprojects.length) {
      setSubId(String(subprojects[0].subproject_id));
    } else if (subprojects && subprojects.length === 0) {
      setSubId("");
    }
  }, [subprojects]);

  const beginEdit = (m) => {
    setEditing(m.milestone_id);
    setDraftPct(m.completion_pct);
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraftPct(0);
  };

  const saveEdit = async (m) => {
    if (draftPct === m.completion_pct) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/milestones/${m.milestone_id}`, { completion_pct: draftPct });
      await mutate(msKey);
      toast.success(`${m.milestone_name}: ${draftPct}%`);
      cancelEdit();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const projectsLoading = !projects;
  const subprojectsLoading = projectId && !subprojects;
  const milestonesLoading = subId && !milestones;

  return (
    <div className="space-y-5">
      <div>
        <div className="rc-eyebrow">Site team</div>
        <div className="rc-title-1 mt-1">Construction Updates</div>
        <div className="rc-callout">Read-only view by default. Tap the edit icon on any milestone to update completion.</div>
      </div>

      <div className="rc-card rc-card-padded flex gap-3 flex-wrap items-end">
        <div className="flex flex-col gap-1">
          <label className="rc-caption uppercase tracking-wider" style={{ fontSize: 10 }}>Project</label>
          <select className="rc-input !w-auto !h-10 !text-[14px]" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={projectsLoading} data-testid="project-select">
            {projectsLoading && <option>Loading…</option>}
            {projects?.map(p => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
            {projects && projects.length === 0 && <option value="">No projects</option>}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="rc-caption uppercase tracking-wider" style={{ fontSize: 10 }}>Subproject / Tower</label>
          <select className="rc-input !w-auto !h-10 !text-[14px]" value={subId} onChange={(e) => setSubId(e.target.value)} disabled={subprojectsLoading || !projectId} data-testid="sub-select">
            {subprojectsLoading && <option>Loading…</option>}
            {subprojects?.map(s => <option key={s.subproject_id} value={s.subproject_id}>{s.subproject_name}</option>)}
            {subprojects && subprojects.length === 0 && <option value="">No subprojects</option>}
          </select>
        </div>
      </div>

      {(projectsLoading || subprojectsLoading || milestonesLoading) && <Skeleton h={300}/>}

      {!milestonesLoading && milestones && milestones.length === 0 && (
        <div className="rc-card rc-card-padded">
          <Empty icon={Hammer} title="No milestones yet" subtitle="This subproject doesn't have any milestones set up. Contact your site engineer to add the construction schedule."/>
        </div>
      )}

      {!milestonesLoading && milestones && milestones.length > 0 && (
        <div className="space-y-3">
          {milestones.map(m => {
            const isEditing = editing === m.milestone_id;
            return (
              <div key={m.milestone_id} className="rc-card rc-card-padded" data-testid={`milestone-row-${m.milestone_id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="rc-headline">{m.milestone_name}</div>
                    <div className="rc-footnote">Planned {fmtDate(m.planned_date)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={m.status}/>
                    {!isEditing ? (
                      <button onClick={() => beginEdit(m)} className="rc-btn rc-btn-sm rc-btn-secondary" data-testid={`edit-${m.milestone_id}`} title="Edit completion">
                        <Pencil size={14}/>
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                    ) : (
                      <>
                        <button onClick={cancelEdit} disabled={saving} className="rc-btn rc-btn-sm rc-btn-ghost" data-testid={`cancel-${m.milestone_id}`}>
                          <X size={14}/>
                          <span className="hidden sm:inline">Cancel</span>
                        </button>
                        <button onClick={() => saveEdit(m)} disabled={saving} className="rc-btn rc-btn-sm rc-btn-primary" data-testid={`save-${m.milestone_id}`}>
                          {saving ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
                          <span className="hidden sm:inline">Save</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Display mode: gradient progress bar */}
                {!isEditing && (
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1 h-2.5 bg-[var(--rc-near-white)] rounded-full overflow-hidden">
                      <div
                        className="h-full"
                        style={{
                          width: `${m.completion_pct}%`,
                          background: m.completion_pct >= 100
                            ? "var(--rc-success)"
                            : "linear-gradient(90deg, #F9A61C, #F27024)",
                          transition: "width 600ms ease",
                        }}
                      />
                    </div>
                    <div className="rc-headline rc-tabular w-12 text-right">{m.completion_pct}%</div>
                  </div>
                )}

                {/* Edit mode: slider + numeric input */}
                {isEditing && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={draftPct}
                        onChange={(e) => setDraftPct(parseInt(e.target.value, 10))}
                        className="flex-1"
                        disabled={saving}
                        data-testid={`pct-slider-${m.milestone_id}`}
                      />
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={draftPct}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!Number.isNaN(v)) setDraftPct(Math.max(0, Math.min(100, v)));
                          }}
                          className="rc-input !h-9 !w-16 !px-2 !text-center rc-tabular !text-[15px]"
                          disabled={saving}
                          data-testid={`pct-input-${m.milestone_id}`}
                        />
                        <span className="rc-headline">%</span>
                      </div>
                    </div>
                    <div className="rc-footnote">
                      Was <b>{m.completion_pct}%</b>, will become <b style={{ color: "var(--rc-orange)" }}>{draftPct}%</b>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
